/**
 * Dropdown — the app's single option picker.
 *
 * Replaces BOTH legacy controls: `v-select` (13 uses) and `v-autocomplete`
 * (~18). To the user they were the same control; the only real difference was
 * filter-as-you-type, so that is the `searchable` prop here rather than a
 * second component.
 *
 * Why this is a custom listbox and not the library's Select: the design
 * system's Select is a native <select>, and a native select structurally
 * cannot render per-option icons, grouped columns, a footer, multi-select
 * chips or a Select All row — all of which the legacy screens use. This is a
 * deliberate, scoped exception to "prefer a library component" in
 * .claude/rules/components.md; the label/hint/error chrome still comes from
 * the library's Field so it matches TextField exactly.
 *
 * What it reproduces from legacy: string or {title,value} options, required
 * marker, clearable, disabled, readOnly, loading, async/dependent options,
 * error messages, hideDetails, grouped menus with headers, per-option icons,
 * a pinned footer, multiple + Select All, and auto-highlight of the first
 * match when searching.
 *
 * What it deliberately does NOT reproduce: the `:items="[]"` + `#prepend-item`
 * hand-built menu (which cost keyboard navigation, type-ahead and
 * selected-option semantics), the dead Vuetify-2 `item-text` prop, and `chips`
 * on single-selects.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Field } from '@components/ui';
import { cx } from '@lib/classNames';

import {
  displayLabel,
  filterGroups,
  flattenGroups,
  isSelected,
  nextValue,
  normalizeGroups,
  selectAllState,
} from './dropdownOptions';
import { CheckIcon, ChevronDownIcon, ClearIcon, SearchIcon } from '@components/common/icons';

import styles from './Dropdown.module.css';

export function Dropdown({
  id,
  label,
  required = false,
  value,
  onChange,
  options,
  groups,
  multiple = false,
  searchable = false,
  selectAll = false,
  placeholder = 'Select',
  hint,
  error,
  disabled = false,
  readOnly = false,
  loading = false,
  clearable = false,
  footer,
  columns = 1,
  hideDetails = false,
  maxListHeight = 320,
  // The open menu's minimum width in px. By default the menu is exactly as
  // wide as the field; a multi-column menu in a narrow field (the sidebar's
  // Service Provider) needs more room, and grows out to the right.
  menuMinWidth,
  className,
  ...rest
}) {
  const reactId = useId();
  const triggerId = id ?? `dropdown-${reactId}`;
  const listboxId = `${triggerId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const allGroups = useMemo(() => normalizeGroups({ options, groups }), [options, groups]);
  const visibleGroups = useMemo(
    () => (searchable ? filterGroups(allGroups, query) : allGroups),
    [allGroups, query, searchable],
  );
  const visibleOptions = useMemo(() => flattenGroups(visibleGroups), [visibleGroups]);
  const allOptions = useMemo(() => flattenGroups(allGroups), [allGroups]);

  const interactive = !disabled && !readOnly;
  const text = displayLabel(allGroups, value, multiple, placeholder);
  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value != null && value !== '';

  // Closed state mirrors the chosen option's icon, as legacy's #selection slot
  // does. Single-select only: with several values picked there is no one icon
  // to show, and the label already collapses to "First (+N others)".
  const selectedIcon = multiple ? null : allOptions.find((o) => o.value === value)?.icon;

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  // Close on outside pointer-down and on Escape, the two ways a menu should
  // always be dismissible regardless of where focus is.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  // Searching re-ranks the list, so re-highlight the first match — legacy's
  // `auto-select-first`, which let Enter pick without arrowing down.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Move focus into the filter box when a searchable menu opens.
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  // Keep the highlighted row in view during keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    // Optional call: jsdom (and older browsers) don't implement scrollIntoView.
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, open]);

  const commit = (option) => {
    if (option.disabled) return;
    onChange?.(nextValue(value, option.value, multiple));
    // A multi-select stays open so several options can be picked in one go.
    if (!multiple) close();
  };

  const { checked: allChecked, indeterminate, allValues } = selectAllState(visibleOptions, value);

  const toggleSelectAll = () => {
    onChange?.(allChecked ? [] : allValues);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(multiple ? [] : '');
  };

  const handleKeyDown = (e) => {
    if (!interactive) return;

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, visibleOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(visibleOptions.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (visibleOptions[activeIndex]) commit(visibleOptions[activeIndex]);
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  // Running index across groups — keyboard nav is flat even though the menu
  // is sectioned, so each row needs its position in the overall sequence.
  let runningIndex = -1;

  return (
    <Field
      label={
        label ? (
          <>
            {label}
            {required ? (
              <span className={styles.required} aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </>
        ) : undefined
      }
      hint={hideDetails ? undefined : hint}
      error={hideDetails ? undefined : error}
      htmlFor={triggerId}
      className={cx(styles.field, className)}
    >
      <div className={styles.root} ref={rootRef}>
        <button
          type="button"
          id={triggerId}
          className={cx(
            styles.trigger,
            open && styles.open,
            error && styles.error,
            !hasValue && styles.placeholder,
          )}
          onClick={() => interactive && setOpen((o) => !o)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-busy={loading || undefined}
          aria-readonly={readOnly || undefined}
          {...rest}
        >
          {selectedIcon ? (
            <span className={styles.optionIcon} aria-hidden="true">
              {selectedIcon}
            </span>
          ) : null}
          <span className={styles.triggerText}>{text}</span>

          <span className={styles.triggerIcons}>
            {clearable && hasValue && interactive ? (
              <span
                role="button"
                tabIndex={-1}
                className={styles.clear}
                onClick={handleClear}
                aria-label={`Clear ${label ?? 'selection'}`}
              >
                <ClearIcon />
              </span>
            ) : null}
            <ChevronDownIcon className={cx(styles.chevron, open && styles.chevronOpen)} />
          </span>

          {loading ? <span className={styles.loadingBar} aria-hidden="true" /> : null}
        </button>

        {/* maxListHeight caps the OPTIONS LIST, not the whole menu: a search box
            or a footer must not shrink how many options you can see. It travels
            to .list as a custom property. */}
        {open ? (
          <div
            className={cx(styles.menu, menuMinWidth && styles.menuWide)}
            style={{
              '--dropdown-list-max': `${maxListHeight}px`,
              ...(menuMinWidth ? { '--dropdown-menu-min': `${menuMinWidth}px` } : null),
            }}
          >
            {searchable ? (
              <div className={styles.search}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  ref={searchRef}
                  type="text"
                  className={styles.searchInput}
                  value={query}
                  placeholder="Search…"
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label={`Filter ${label ?? 'options'}`}
                  aria-controls={listboxId}
                />
              </div>
            ) : null}

            <ul
              id={listboxId}
              ref={listRef}
              role="listbox"
              aria-multiselectable={multiple || undefined}
              aria-label={label ?? 'Options'}
              className={cx(styles.list, columns > 1 && styles.columns)}
              style={columns > 1 ? { '--dropdown-columns': columns } : undefined}
            >
              {selectAll && multiple ? (
                <li className={styles.selectAll}>
                  <button type="button" className={styles.option} onClick={toggleSelectAll}>
                    <span className={styles.optionLabel}>Select All</span>
                    <span className={cx(styles.check, allChecked && styles.checked)}>
                      {allChecked ? <CheckIcon /> : null}
                      {indeterminate ? <span className={styles.dash} /> : null}
                    </span>
                  </button>
                </li>
              ) : null}

              {visibleGroups.length === 0 ? (
                <li className={styles.empty}>No matching options</li>
              ) : null}

              {visibleGroups.map((group) => (
                <li key={group.label ?? '__ungrouped'} className={styles.group}>
                  {group.label ? (
                    <div className={styles.groupLabel} role="presentation">
                      {group.label}
                    </div>
                  ) : null}
                  <ul role="group" aria-label={group.label} className={styles.groupList}>
                    {group.options.map((option) => {
                      runningIndex += 1;
                      const index = runningIndex;
                      const selected = isSelected(value, option.value, multiple);
                      return (
                        <li key={String(option.value)}>
                          <button
                            type="button"
                            data-index={index}
                            role="option"
                            aria-selected={selected}
                            disabled={option.disabled}
                            className={cx(
                              styles.option,
                              selected && styles.selected,
                              index === activeIndex && styles.active,
                            )}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => commit(option)}
                          >
                            {option.icon ? (
                              <span className={styles.optionIcon} aria-hidden="true">
                                {option.icon}
                              </span>
                            ) : null}
                            <span className={styles.optionLabel}>{option.label}</span>
                            {/* Last child: .optionLabel is flex:1, so the tick
                                is pushed to the right edge of the row. */}
                            <span className={cx(styles.check, selected && styles.checked)}>
                              {selected ? <CheckIcon /> : null}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>

            {footer ? <div className={styles.footer}>{footer}</div> : null}
          </div>
        ) : null}
      </div>
    </Field>
  );
}

export default Dropdown;
